import { getOneAccount } from "../../accounts";
import axios from "axios";

import { AccountType } from "../../../shared/types/accounts";

import { JsonResp, unwrapJsonResp } from "../../JsonResp";
import {
  type BusinessTokenType,
  FBAccount,
  FacebookTokenAuthResponse,
  FBPageExtended,
  fbReadMe,
  fbReadPagesExtended,
  getFacebookAuthType,
} from "./fb-sdk-utils";
import {
  authoriseFacebook,
  ConnectionHandlePageBody,
  ConnectionHandleReconnectBody,
  ConnectionHandleSession,
  loginArgs,
  toFacebookPickerPage,
} from "./authorise-facebook";
import { toast } from "react-toastify";
import {
  getUserFriendlyErrorMessage,
  UserFriendlyError,
} from "../../UserFriendlyError";
import { FACEBOOK_GROUPS_REMOVED_MSG } from "../../../constants/TooltipMessages";

export type {
  FBAccount,
  FacebookAuthResponse,
  FacebookCodeAuthResponse,
  FacebookTokenAuthResponse,
  FBPageExtended,
} from "./fb-sdk-utils";

export const RequiredFacebookScopes = {
  posting: [
    "pages_show_list",
    "pages_manage_posts",
    "pages_manage_metadata",
    "pages_read_engagement",
    "pages_read_user_content",
  ],
  insights: ["read_insights", "pages_read_engagement"],
  inbox: ["pages_messaging"],
  inboxComments: ["pages_manage_engagement"],
  // temporarily add business_management here so the user
  // isn't prompted to reconnect FB
  insightsAds: ["ads_read"],
  adPosts: ["pages_manage_ads", "ads_management"],
};

export const FacebookScopes = [
  ...RequiredFacebookScopes.posting,
  // Request business access at login, but do not require it for Page posting.
  "business_management",
  ...RequiredFacebookScopes.insights,
  ...RequiredFacebookScopes.inbox,
];

export const getFacebookAdsScopes = () => {
  return (RequiredFacebookScopes.insightsAds || []).concat(
    RequiredFacebookScopes.adPosts
  );
};

/** `account` is null when the login produced a connection handle (SKE-4810). */
export type FacebookPagesSet =
  | {
      account: FBAccount;
      pages: FBPageExtended[];
      session: FacebookTokenAuthResponse;
    }
  | {
      account: null;
      pages: FBPageExtended[];
      session: ConnectionHandleSession;
    };

export async function getFacebookPages({
  user,
  businessTokenType,
}: {
  user: any;
  /** SKE-5310. `user` when the person chose "Connect another way". */
  businessTokenType?: BusinessTokenType;
}): Promise<FacebookPagesSet> {
  try {
    const isInboxCommentsEnabled = true;
    console.log("FB Login");
    const authorisation = await authoriseFacebook(
      ...loginArgs(
        FacebookScopes.concat(
          isInboxCommentsEnabled ? RequiredFacebookScopes.inboxComments : []
        ).concat(getFacebookAdsScopes()),
        businessTokenType
      )
    );
    if (authorisation.kind === "handle") {
      return {
        account: null,
        pages: authorisation.pages.map(toFacebookPickerPage),
        session: { connectionHandle: authorisation.connectionHandle },
      };
    }
    const auth = authorisation.auth;
    console.log("FB login complete, reading me");
    const account = await fbReadMe(auth);
    console.log("Completed read me, reading pages");
    if (user.preferences?.useShortPagesFbApi)
      console.log(
        "Using shorter pagination limit due to useShortPagesFbApi boolean being true."
      );
    const pages = await fbReadPagesExtended(
      auth,
      user.preferences?.useShortPagesFbApi
    );
    console.log("FB pages read");
    return { account, pages, session: auth };
  } catch (e) {
    console.error(e);
    toast.error(getUserFriendlyErrorMessage(e as Error));
    throw e;
  }
}

export type FacebookPageConnectPayload =
  | {
      account: FBAccount;
      page: FBPageExtended;
      session: FacebookTokenAuthResponse;
    }
  | {
      account: null;
      page: FBPageExtended;
      session: ConnectionHandleSession;
    };

export async function addFacebookPageAccount(
  payload: FacebookPageConnectPayload
): Promise<AccountType> {
  if (payload.account === null) {
    const handleBody: ConnectionHandlePageBody = {
      connectionHandle: payload.session.connectionHandle,
      pageId: payload.page.id,
    };
    const handleResponse = await axios.request<JsonResp<AccountType[]>>({
      method: "post",
      url: "/accounts/facebook",
      data: handleBody,
    });
    return unwrapJsonResp(handleResponse.data)[0];
  }
  const { account, page, session } = payload;
  const reqData = {
    session: session,
    facebookAuthType: getFacebookAuthType(),
    user_token: session.accessToken,
    platformType: "FB",
    platformUserId: account.platformUserId,
    first_name: account.first_name,
    last_name: account.last_name,
    login: page.name_with_location_descriptor || page.name,
    defaultPage: page,
    pages: [page],
    profilePictureUrl: `https://graph.facebook.com/${page.id}/picture?type=large`,
  };

  const response = await axios.request<JsonResp<AccountType[]>>({
    method: "post",
    url: "/accounts/facebook",
    data: reqData,
  });

  const accounts = unwrapJsonResp(response.data);
  return accounts[0];
}

export async function reconnectFacebookAccount(account: AccountType) {
  // SKE-4694. Groups publishing is removed; never open Facebook login for one.
  if (account.isGroup) {
    throw new UserFriendlyError(FACEBOOK_GROUPS_REMOVED_MSG);
  }
  const scope = FacebookScopes.concat(getFacebookAdsScopes());
  const isInboxEnabled = true;
  const isInboxCommentsEnabled = true;
  const authorisation = await authoriseFacebook(
    scope
      .concat(isInboxEnabled ? RequiredFacebookScopes.inbox : [])
      .concat(
        isInboxCommentsEnabled ? RequiredFacebookScopes.inboxComments : []
      ),
    { useBusinessLogin: true }
  );

  if (authorisation.kind === "handle") {
    const handleBody: ConnectionHandleReconnectBody = {
      reconnectedAccount: account._id,
      connectionHandle: authorisation.connectionHandle,
    };
    await axios.request({
      method: "post",
      url: "/accounts/reconnectFacebook",
      data: handleBody,
    });
    return await getOneAccount(account._id);
  }
  const auth = authorisation.auth;

  const data = {
    reconnectedAccount: account._id,
    authResponseData: auth,
    facebookAuthType: getFacebookAuthType(),
  };

  await axios.request({
    method: "post",
    url: "/accounts/reconnectFacebook",
    data,
  });

  return await getOneAccount(account._id);
}
